import { createTaxRateApi, updateTaxRateApi } from "@/api/services/global/master";
import { TGetTaxRateListOutputDto } from "@/api/services/global/master/dtos/taxRate/get-master-tax-rate-list.dto";
import { Button } from "@/components/elements/Button";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { InputDatePicker } from "@/components/elements/InputDatePicker";
import { Modal } from "@/components/elements/Modal";
import { Select } from "@/components/elements/Select";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { DATE_FORMAT_FOR_API } from "@/constants/datetime";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { masterDataTaxRateSchema } from "@/features/master-data/schema/masterDataTaxRateSchema";
import { TMasterTaxRateForm } from "@/features/master-data/types/masterDataTaxRateForm";
import { TAX_RATE_PERCENT_DECIMAL_PLACES, percentToRate, rateToPercent } from "@/features/master-data/utils/taxRate";
import { text } from "@/theme/colors";
import { dayjs } from "@/utils/dayjs";
import { setErrorForm, showError } from "@/utils/error";
import { toNumber } from "@/utils/number";
import { toast } from "@/utils/toast";
import { yupResolver } from "@hookform/resolvers/yup";
import { Grid, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { Dayjs } from "dayjs";
import { FC, useMemo } from "react";
import { useForm } from "react-hook-form";

type TTaxRateFormModalProps = {
  onClose: () => void;
  isOpen: boolean;
  selectedTaxRate?: TGetTaxRateListOutputDto;
  /** 表示名の重複を送信前にチェックするための既存行 */
  taxRates: TGetTaxRateListOutputDto[];
};

/** API 送信用に日付を YYYY-MM-DD へ変換する */
const toApiDate = (date?: Dayjs | null) => (date ? dayjs(date).format(DATE_FORMAT_FOR_API) : null);

export const TaxRateFormModal: FC<TTaxRateFormModalProps> = ({ onClose, isOpen, selectedTaxRate, taxRates }) => {
  const queryClient = useQueryClient();

  const defaultValues = useMemo(
    () => ({
      taxRateSeries: selectedTaxRate ? selectedTaxRate.taxRateSeries : undefined,
      displayName: selectedTaxRate ? selectedTaxRate.displayName : undefined,
      taxCategory: selectedTaxRate ? selectedTaxRate.taxCategory : undefined,
      ratePercent: selectedTaxRate ? rateToPercent(selectedTaxRate.rate) : undefined,
      effectiveFrom: selectedTaxRate?.effectiveFrom ? dayjs(selectedTaxRate.effectiveFrom) : undefined,
      effectiveTo: selectedTaxRate?.effectiveTo ? dayjs(selectedTaxRate.effectiveTo) : null,
    }),
    [selectedTaxRate],
  );

  const methods = useForm<TMasterTaxRateForm>({
    resolver: yupResolver(masterDataTaxRateSchema),
    mode: "onBlur",
    defaultValues,
  });

  const { control, setError } = methods;

  /** 表示名は請求・商品の選択肢に出るため重複を許さない（サーバー側でも 409 で弾かれる） */
  const isDuplicatedDisplayName = (displayName: string) =>
    taxRates.some((item) => item.displayName === displayName && toNumber(item.id) !== toNumber(selectedTaxRate?.id));

  const onSubmit = async (data: TMasterTaxRateForm) => {
    if (isDuplicatedDisplayName(data.displayName)) {
      setError("displayName", { message: `「表示名」（${data.displayName}）はすでに登録されています。` });
      return;
    }

    try {
      const payload = {
        taxRateSeries: data.taxRateSeries,
        displayName: data.displayName,
        taxCategory: data.taxCategory,
        rate: percentToRate(data.ratePercent),
        effectiveFrom: toApiDate(data.effectiveFrom) ?? "",
        effectiveTo: toApiDate(data.effectiveTo),
      };

      if (selectedTaxRate) {
        const rs = await updateTaxRateApi(toNumber(selectedTaxRate.id), payload);
        toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      } else {
        const rs = await createTaxRateApi(payload);
        toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      }
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GLOBAL.MASTER_TAX_RATE] });
      onClose();
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
    }
  };

  return (
    <Modal title={selectedTaxRate ? "編集" : "追加"} isOpen={isOpen} enableFooter={false} onClose={onClose} enableCloseIcon={false} width={480}>
      <Form methods={methods} onSubmit={onSubmit} schema={masterDataTaxRateSchema}>
        <Stack gap={2} mt={2}>
          <Typography variant="cap12" color={text.secondary}>
            税率改定時は既存行を書き換えず、既存行に「適用終了日」を設定し、改定日を「適用開始日」とする新しい行を追加してください。書き換えると過去の請求の税率が変わってしまいます。
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <FormField control={control} name="taxRateSeries">
                <Input placeholder="入力してください" />
              </FormField>
            </Grid>
            <Grid item xs={6}>
              <FormField control={control} name="displayName">
                <Input placeholder="入力してください" />
              </FormField>
            </Grid>
            <Grid item xs={6}>
              <FormField control={control} name="taxCategory">
                {/* 課税区分の選択肢は BE の enum から取得する */}
                <Select enumName="ETaxCategory" />
              </FormField>
            </Grid>
            <Grid item xs={6}>
              {/* 入力は % 単位。送信時に小数表記（10 → 0.1）へ変換する */}
              <FormField control={control} name="ratePercent" helper="％で入力してください（例: 10）">
                <Input placeholder="入力してください" endAdornment="%" decimalPlaces={TAX_RATE_PERCENT_DECIMAL_PLACES} />
              </FormField>
            </Grid>
            <Grid item xs={6}>
              <FormField control={control} name="effectiveFrom">
                <InputDatePicker />
              </FormField>
            </Grid>
            <Grid item xs={6}>
              <FormField control={control} name="effectiveTo" helper="未入力の場合は無期限になります。">
                <InputDatePicker />
              </FormField>
            </Grid>
          </Grid>
        </Stack>
        <Stack flexDirection="row" justifyContent="space-between" gap="24px" mt={3}>
          <Button size="sm" variant="secondary" onClick={onClose}>
            キャンセル
          </Button>
          <Button size="sm" type="submit">
            保存
          </Button>
        </Stack>
      </Form>
    </Modal>
  );
};
